import { useMemo, useState } from "react";
import { videos } from "@/mocks/videos";
import { categories, durationFilters } from "@/mocks/categories";
import VideoCard from "@/components/base/VideoCard";
import SectionHeader from "@/components/base/SectionHeader";

interface VideoLibraryProps {
  keyword: string;
  category: string;
  length: string;
  onKeyword: (v: string) => void;
  onCategory: (v: string) => void;
  onLength: (v: string) => void;
}

const PAGE = 8;

export default function VideoLibrary({
  keyword,
  category,
  length,
  onKeyword,
  onCategory,
  onLength,
}: VideoLibraryProps) {
  const [visible, setVisible] = useState(PAGE);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return videos.filter((v) => {
      const inCat = category === "all" || v.categoryKey === category;
      const inLen = length === "all" || v.length === length;
      const inKw =
        !kw ||
        v.title.toLowerCase().includes(kw) ||
        v.category.toLowerCase().includes(kw) ||
        v.tags.some((t) => t.toLowerCase().includes(kw));
      return inCat && inLen && inKw;
    });
  }, [keyword, category, length]);

  const shown = filtered.slice(0, visible);

  const categoryPills = [{ key: "all", name: "全部", icon: "ri-apps-2-line" }, ...categories];

  return (
    <section id="library" className="w-full bg-background-50 px-4 py-14 md:px-6 md:py-20">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8">
        <SectionHeader
          eyebrow="视频库"
          title="短视频集锦与完整长视频，任你筛选"
          subtitle="按类型与时长快速定位你想看的内容，也支持关键词搜索。以下为公开合法的娱乐内容聚合。"
        />

        <div className="flex flex-col gap-5 rounded-2xl border border-background-200/70 bg-background-100/60 p-4 md:p-5">
          <div className="flex flex-col gap-3">
            <span className="flex items-center gap-2 text-sm font-bold text-foreground-800">
              <i className="ri-list-settings-line text-primary-500" />
              按类型筛选
            </span>
            <div className="flex flex-wrap gap-2">
              {categoryPills.map((pill) => {
                const active = category === pill.key;
                return (
                  <button
                    key={pill.key}
                    type="button"
                    onClick={() => {
                      onCategory(pill.key);
                      setVisible(PAGE);
                    }}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      active
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300/60 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    <i className={`${pill.icon} text-[13px]`} />
                    {pill.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <span className="flex items-center gap-2 text-sm font-bold text-foreground-800">
              <i className="ri-time-line text-primary-500" />
              按时长筛选
            </span>
            <div className="flex flex-wrap gap-2">
              {durationFilters.map((d) => {
                const active = length === d.key;
                return (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => {
                      onLength(d.key);
                      setVisible(PAGE);
                    }}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      active
                        ? "bg-accent-500 text-background-50"
                        : "border border-background-300/60 bg-background-50 text-foreground-700 hover:border-accent-400 hover:text-accent-700"
                    }`}
                  >
                    <i className="ri-disc-line text-[13px]" />
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-background-200/70 pt-4">
            <span className="flex items-center gap-2 text-sm font-bold text-foreground-800">
              <i className="ri-search-2-line text-primary-500" />
              关键词搜索
            </span>
            <label className="relative max-w-md">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-foreground-500" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => onKeyword(e.target.value)}
                placeholder="输入关键词，如“红毯”“综艺”…"
                className="w-full rounded-full bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-800 outline-none ring-1 ring-background-300/60 placeholder:text-foreground-500 focus:ring-2 focus:ring-primary-400"
              />
            </label>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm text-foreground-600">
            共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 条吃瓜内容
            {keyword.trim() && (
              <>
                ，关键词“<span className="font-bold text-primary-600">{keyword.trim()}</span>”
              </>
            )}
          </p>
          {(category !== "all" || length !== "all") && (
            <button
              type="button"
              onClick={() => {
                onCategory("all");
                onLength("all");
                setVisible(PAGE);
              }}
              className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-foreground-500 transition-colors hover:text-primary-600"
            >
              <i className="ri-refresh-line" />
              清除筛选
            </button>
          )}
        </div>

        {shown.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {shown.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-background-300 bg-background-100/50 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <p className="text-base font-bold text-foreground-800">没有找到匹配的视频</p>
            <p className="text-sm text-foreground-500">换个关键词或清除筛选条件再试试吧</p>
            <button
              type="button"
              onClick={() => {
                onKeyword("");
                onCategory("all");
                onLength("all");
                setVisible(PAGE);
              }}
              className="mt-1 flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-restart-line" />
              重置所有筛选
            </button>
          </div>
        )}

        {visible < filtered.length && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setVisible((v) => v + PAGE)}
              className="flex items-center gap-2 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-6 py-2.5 text-sm font-bold text-primary-600 transition-colors hover:bg-primary-100"
            >
              <i className="ri-add-line text-base" />
              加载更多视频
            </button>
          </div>
        )}
      </div>
    </section>
  );
}